import { Reducer } from 'redux';
import { update } from '../helpers/immutabilityHelper'
import * as Dtos from '../dtos/Tmd.dtos';

import {
    ACTION_ROLES_LOAD,
    ACTION_ROLES_LOAD_BY_ID,
    ACTION_ROLES_LOAD_FAILURE,
    ACTION_ROLES_LOAD_SUCCESS,

    ACTION_ROLES_SAVE,
    ACTION_ROLES_SAVE_SUCCESS,
    ACTION_ROLES_SAVE_FAILURE,

    ACTION_ROLES_CREATE,
    ACTION_ROLES_CREATE_SUCCESS,
    ACTION_ROLES_CREATE_FAILURE,

    ACTION_ROLES_VALIDATE,
    ACTION_ROLES_VALIDATE_SUCCESS,
    ACTION_ROLES_VALIDATE_FAILURE,

    ACTION_ROLES_CLEAR,
    ACTION_ROLES_FILTER,

    RoleAction
} from '../actions/role';
import { RequestState } from "../enumerations/RequestState";
import { IRequestState, IFormFilter } from "./index";
import { updateFailureIndexToId } from "../helpers/formErrorHelper";

export interface IRoleState {
    formData: Dtos.Role | Dtos.Role[];
    formState: Dtos.FormState;
    formProperties: Dtos.FormProperty[];
    validationFailures: Dtos.ResponseError[];
    lookups: Dtos.Lookup[];
    modules: Dtos.ModulePermission[];
    loadState: IRequestState;
    saveState: IRequestState;
    updateState: IRequestState;
    createState: IRequestState;
    validateState: IRequestState;
    formFilter: IFormFilter;
    formFilteredData: Dtos.Role[];
    formLength: number;
}

const initialState: IRoleState = {
    formData: undefined,
    formState: undefined,
    formProperties: undefined,
    validationFailures: undefined,
    lookups: undefined,
    modules: undefined,
    loadState: {
        status: RequestState.None
    } as IRequestState,
    saveState: {
        status: RequestState.None
    } as IRequestState,
    updateState: {
        status: RequestState.None
    } as IRequestState,
    createState: {
        status: RequestState.None
    } as IRequestState,
    validateState: {
        status: RequestState.None
    } as IRequestState,
    formFilter: {
        search: "",
        showInActive: false
    },
    formFilteredData: [],
    formLength: 0
};


function loading(state: IRoleState, _isLoading: boolean): IRoleState {
    const newState: IRoleState = update(state, {
        loadState: {
            $set: {
                status: RequestState.Pending
            } as IRequestState
        }
    });

    return newState;
}

function loadSuccess(state: IRoleState,
    formData: Dtos.Role | Dtos.Role[],
    formState: Dtos.FormState,
    formProperties: Dtos.FormProperty[],
    lookups: Dtos.Lookup[],
    modules: Dtos.ModulePermission[],
    responseStatus: Dtos.ResponseStatus
): IRoleState {
    const newState: IRoleState = update(state, {
        formData: {
            $set: formData
        },
        formState: {
            $set: formState
        },
        formProperties: {
            $set: formProperties
        },
        lookups: {
            $set: lookups
        },
        modules: {
            $set: modules
        },
        loadState: {
            $set: {
                status: RequestState.Success,
                errorCode: responseStatus ? responseStatus.errorCode : undefined,
                errorMessage: responseStatus ? responseStatus.message : undefined,
                errors: responseStatus ? responseStatus.errors : undefined,
                meta: responseStatus ? responseStatus.meta : undefined
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus.errors, formData) : undefined
        }
    });

    return filter(newState, state.formFilter);
}

function filter(state: IRoleState, formFilter: IFormFilter): IRoleState {

    const items: Dtos.Role[] | undefined =
        state.formData instanceof Array ?
            state.formData :
            undefined;

    const formLength = items ? items.length : 0;

    const formFilteredData = items ? items.filter(p => (p.active == true || formFilter.showInActive) &&
        (formFilter.search == "" || p.value.toLowerCase().indexOf(formFilter.search.toLowerCase()) > -1)) : undefined;

    const newState: IRoleState = update(state, {
        formFilter: {
            $set: formFilter
        },
        formFilteredData: {
            $set: formFilteredData
        },
        formLength: {
            $set: formLength
        }
    });

    return newState;
}

function loadFailure(state: IRoleState, responseStatus: Dtos.ResponseStatus): IRoleState {
    const newState: IRoleState = update(state, {
        loadState: {
            $set: {
                status: RequestState.Failure,
                errorCode: responseStatus.errorCode,
                errorMessage: responseStatus.message,
                errors: responseStatus.errors
            } as IRequestState
        }
    });

    return newState;
}

function clear(state: IRoleState, propertiesToClear?: string[]): IRoleState {

    if (!propertiesToClear) {
        let formFilter = state.formFilter;
        const newState: IRoleState = update(initialState, {
            formFilter: {
                $set: formFilter
            }
        });
        return newState;
    }

    let stateModifier: any = {};

    propertiesToClear.forEach(propertyToClear => {
        stateModifier[propertyToClear] = { $set: initialState[propertyToClear] }
    });

    const newState = update(state, stateModifier);

    return newState;
}

function saving(state: IRoleState, _isSaving: boolean): IRoleState {
    const newState: IRoleState = update(state, {
        saveState: {
            $set: {
                status: RequestState.Pending
            } as IRequestState
        }
    });

    return newState;
}

function saveSuccess(
    state: IRoleState,
    formData: Dtos.Role | Dtos.Role[],
    formState: Dtos.FormState,
    responseStatus: Dtos.ResponseStatus
): IRoleState {
    const newState: IRoleState = update(state, {
        formData: {
            $set: formData
        },
        formState: {
            $set: formState
        },
        saveState: {
            $set: {
                status: RequestState.Success,
                errorCode: responseStatus ? responseStatus.errorCode : undefined,
                errorMessage: responseStatus ? responseStatus.message : undefined,
                errors: responseStatus ? responseStatus.errors : undefined,
                meta: responseStatus ? responseStatus.meta : undefined
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus.errors, formData) : undefined
        }
    });

    return newState;
}

function saveFailure(state: IRoleState, responseStatus: Dtos.ResponseStatus): IRoleState {
    const newState: IRoleState = update(state, {
        saveState: {
            $set: {
                status: RequestState.Failure,
                errorCode: responseStatus.errorCode,
                errorMessage: responseStatus.message,
                errors: responseStatus.errors,
                meta: responseStatus.meta
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus.errors, state.formData) : undefined
        }
    });

    return newState;
}

function creating(state: IRoleState, _isCreating: boolean): IRoleState {
    const newState: IRoleState = update(state, {
        createState: {
            $set: {
                status: RequestState.Pending
            } as IRequestState
        }
    });

    return newState;
}

function createSuccess(
    state: IRoleState,
    formData: Dtos.Role,
    formState: Dtos.FormState,
    formProperties: Dtos.FormProperty[],
    lookups: Dtos.Lookup[]
): IRoleState {
    const newState: IRoleState = update(state, {
        formData: {
            $set: formData
        },
        formState: {
            $set: formState
        },
        formProperties: {
            $set: formProperties
        },
        lookups: {
            $set: lookups
        },
        createState: {
            $set: {
                status: RequestState.Success
            } as IRequestState
        }
    });

    return newState;
}

function createFailure(state: IRoleState, responseStatus: Dtos.ResponseStatus): IRoleState {
    const newState: IRoleState = update(state, {
        createState: {
            $set: {
                status: RequestState.Failure,
                errorCode: responseStatus.errorCode,
                errorMessage: responseStatus.message,
                errors: responseStatus.errors
            } as IRequestState
        }
    });

    return newState;
}

function validating(state: IRoleState, _isValidating: boolean): IRoleState {
    const newState: IRoleState = update(state, {
        validateState: {
            $set: {
                status: RequestState.Pending
            } as IRequestState
        }
    });

    return newState;
}

function validateSuccess(
    state: IRoleState,
    formData: Dtos.Role,
    formState: Dtos.FormState,
    responseStatus: Dtos.ResponseStatus
): IRoleState {
    const newState: IRoleState = update(state, {
        formData: {
            $set: formData
        },
        formState: {
            $set: formState
        },
        validateState: {
            $set: {
                status: RequestState.Success,
                errorCode: responseStatus ? responseStatus.errorCode : undefined,
                errorMessage: responseStatus ? responseStatus.message : undefined,
                errors: responseStatus ? responseStatus.errors : undefined,
                meta: responseStatus ? responseStatus.meta : undefined
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus.errors, formData) : undefined
        }
    });

    return newState;
}

function validateFailure(state: IRoleState, responseStatus: Dtos.ResponseStatus): IRoleState {
    const newState: IRoleState = update(state, {
        validateState: {
            $set: {
                status: RequestState.Failure,
                errorCode: responseStatus.errorCode,
                errorMessage: responseStatus.message,
                errors: responseStatus.errors,
                meta: responseStatus.meta
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus.errors, state.formData) : undefined
        }
    });

    return newState;
}

const RoleReducer: Reducer<IRoleState> = (state: IRoleState = initialState, action: RoleAction) => {
    switch (action.type) {
        case ACTION_ROLES_LOAD:
        case ACTION_ROLES_LOAD_BY_ID:
            return loading(state, true);
        case ACTION_ROLES_LOAD_SUCCESS:
            return loadSuccess(state, action.formData, action.formState, action.formProperties, action.lookups, action.modules, action.responseStatus);
        case ACTION_ROLES_LOAD_FAILURE:
            return loadFailure(state, action.responseStatus);
        case ACTION_ROLES_CLEAR:
            return clear(state, action.propertiesToClear);
        case ACTION_ROLES_SAVE:
            return saving(state, true);
        case ACTION_ROLES_SAVE_SUCCESS:
            return saveSuccess(state, action.formData, action.formState, action.responseStatus);
        case ACTION_ROLES_SAVE_FAILURE:
            return saveFailure(state, action.responseStatus);
        case ACTION_ROLES_CREATE:
            return creating(state, true);
        case ACTION_ROLES_CREATE_SUCCESS:
            return createSuccess(state, action.formData, action.formState, action.formProperties, action.lookups);
        case ACTION_ROLES_CREATE_FAILURE:
            return createFailure(state, action.responseStatus);
        case ACTION_ROLES_VALIDATE:
            return validating(state, true);
        case ACTION_ROLES_VALIDATE_SUCCESS:
            return validateSuccess(state, action.formData, action.formState, action.responseStatus);
        case ACTION_ROLES_VALIDATE_FAILURE:
            return validateFailure(state, action.responseStatus);
        case ACTION_ROLES_FILTER:
            return filter(state, action.formFilter);

    }

    return state;
}

export default RoleReducer;